'use client';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  FC,
  SVGProps,
} from 'react';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import {
  AATConfig,
  AFSConfig,
  AllIsWellConfig,
  AumeConfig,
  CalabriConfig,
  CommunityRXConfig,
  CosmoPrepConfig,
  CWJCConfig,
  DefaultConfig,
  ElevateLearningConfig,
  HealingConfig,
  LeverageConfig,
  LiftRAndRConfig,
  ProjectRestartConfig,
  SD171Config,
  SeedlyngConfig,
  UpstartTaxConfig,
  WealthWithLeeConfig,
  WorkforceConfig,
  BriidgeTheGapConfig,
  CodeForChangeConfig,
  MentalMattersConfig,
  KitneticConfig,
  PelicanBayouConfig,
} from '@/config';

export type LogoSrc = { src: string }; // current PNG import shape
export type LogoSvg = FC<SVGProps<SVGSVGElement>>; // an inline SVG component

// TODO::Set colors differently
export interface Config {
  name: string;
  allowPersonalCourses: boolean;
  logo: LogoSrc | LogoSvg;
  colors: {
    brand_primary: string;
    brand_primary_hover: string;
    brand_primary_highlight: string;
    brand_primary_mid: string;
    brand_primary_dark: string;
    brand_secondary: string;
    brand_accent_start: string;
    brand_accent_end: string;
    brand_accent_light_start: string;
    brand_accent_light_end: string;
    brand_complementary_accent: string;
    brand_auth_gradient_mid: string;
    brand_auth_gradient_end: string;
    brand_auth_warp_mid: string;
  };
}

const ConfigContext = createContext<Config | null>(null);

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<Config | null>(null);

  useEffect(() => {
    const setCSSVariable = (property: string, value: string) => {
      try {
        if (typeof document !== 'undefined' && document.documentElement?.style) {
          document.documentElement.style.setProperty(property, value);
        }
      } catch (error) {
        console.warn(`Failed to set CSS variable ${property}:`, error);
      }
    };

    const loadConfig = () => {
      const hostname = window.location.hostname;

      const configMap: Record<string, Config> = {
        sd171: SD171Config,
        aeyl: WorkforceConfig,
        seedlyng: SeedlyngConfig,
        demo: DefaultConfig,
        afs: AFSConfig,
        projectrestart: ProjectRestartConfig,
        liftrandr: LiftRAndRConfig,
        upstarttax: UpstartTaxConfig,
        aboveallthings: AATConfig,
        leverage: LeverageConfig,
        elevatelearning: ElevateLearningConfig,
        cosmoprep: CosmoPrepConfig,
        healing: HealingConfig,
        calabri: CalabriConfig,
        aume: AumeConfig,
        alliswellhca: AllIsWellConfig,
        christianjobcorpstx: CWJCConfig,
        communityrxhelp: CommunityRXConfig,
        wealthwithlee: WealthWithLeeConfig,
        briidgethegap: BriidgeTheGapConfig,
        pathfinder: ProjectRestartConfig,
        thecodeforchange: CodeForChangeConfig,
        mentalmatters: MentalMattersConfig,
        kitnetic: KitneticConfig,
        pelicanbayou: PelicanBayouConfig,
      };

      const config: Config =
        Object.entries(configMap).find(([key]) => hostname.includes(key))?.[1] || DefaultConfig;
      setConfig(config);

      setCSSVariable('--color-brand', config.colors.brand_primary);
      setCSSVariable('--color-brand-highlight', config.colors.brand_primary_highlight);
      setCSSVariable('--color-brand-mid', config.colors.brand_primary_mid);
      setCSSVariable('--color-brand-dark', config.colors.brand_primary_dark);
      setCSSVariable('--color-brand-secondary', config.colors.brand_secondary);
      setCSSVariable('--color-brand-hover', config.colors.brand_primary_hover);
      setCSSVariable('--color-brand-accent-start', config.colors.brand_accent_start);
      setCSSVariable('--color-brand-accent-end', config.colors.brand_accent_end);
      setCSSVariable('--color-brand-accent-light-start', config.colors.brand_accent_light_start);
      setCSSVariable('--color-brand-accent-light-end', config.colors.brand_accent_light_end);
      setCSSVariable('--color-brand-auth-accent-pop', config.colors.brand_complementary_accent);
      setCSSVariable('--color-brand-auth-gradient-mid', config.colors.brand_auth_gradient_mid);
      setCSSVariable('--color-brand-auth-gradient-end', config.colors.brand_auth_gradient_end);
      setCSSVariable('--color-brand-auth-warp-mid', config.colors.brand_auth_warp_mid);
    };

    loadConfig();
  }, []);

  // Hydrate user progress from IndexedDB (fallback to network) on initial mount
  useEffect(() => {
    void UserProgressStorageService.validateUserCourseProgressCookie();
  }, []);

  if (!config) {
    return <div>Loading...</div>;
  }

  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
};

export const useConfig = (): Config | null => useContext(ConfigContext);
